import axios from 'axios';
import { client } from './axiosClient';

// The JTL door. Cognito does the JTL login (federation to JTL's identity service); this module
// only talks to Cognito's hosted endpoints and to our backend's arrival/link endpoints.

// The pool's hosted domain is `tax-<env>` (see cdk/backend_stack.py in api-backend). The env
// variable is a local override, for pointing a dev checkout at another pool.
const COGNITO_DOMAIN: string =
  import.meta.env.VITE_JTL_COGNITO_DOMAIN ??
  `https://tax-${import.meta.env.VITE_APP_ENV ?? 'development'}.auth.${
    import.meta.env.VITE_APP_REGION ?? 'eu-central-1'
  }.amazoncognito.com`;
const CLIENT_ID: string = import.meta.env.VITE_APP_USERPOOL_CLIENT_ID ?? '';
export const JTL_CALLBACK_PATH = '/jtl-login/callback';

export const JTL_RELOGIN_PATH = '/jtl-login/relogin';

export const jtlCallbackUrl = () =>
  `${window.location.origin}${JTL_CALLBACK_PATH}`;

// Cognito keeps a hosted-UI session cookie of its own. After Linking (where the stray profile
// is deleted) that cookie still names the deleted profile, so a plain re-authorize issues a dead
// code. Clear it first; Cognito then lands on /jtl-login/relogin, which reopens the tile URL.
export const jtlRelogoutUrl = () => {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: `${window.location.origin}${JTL_RELOGIN_PATH}`,
  });
  return `${COGNITO_DOMAIN}/logout?${params.toString()}`;
};

// What the Hub tile would point at: a static link, no per-attempt values.
export const jtlTileUrl = () => {
  const params = new URLSearchParams({
    identity_provider: 'ZITADEL',
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: jtlCallbackUrl(),
    scope: 'openid email profile aws.cognito.signin.user.admin',
  });
  return `${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`;
};

export interface CognitoTokens {
  access_token: string;
  id_token: string;
  refresh_token: string;
  expires_in: number;
}

export const exchangeCognitoCode = async (code: string) => {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: CLIENT_ID,
    code,
    redirect_uri: jtlCallbackUrl(),
  });
  const res = await axios.post<CognitoTokens>(
    `${COGNITO_DOMAIN}/oauth2/token`,
    body,
    { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
  );
  return res.data;
};

export type JtlArrivalOutcome =
  | 'linked'
  | 'no_link'
  | 'no_account'
  | 'tenant_mismatch'
  | 'not_jtl';

export interface JtlArrival {
  outcome: JtlArrivalOutcome;
  email?: string;
  jtl_sub?: string;
  tenant_id?: string;
  linked_tenant_id?: string;
  user_email?: string;
}

export const jtlArrival = async () => {
  const res = await client.get<JtlArrival>('/auth/jtl/arrival');
  return res.data;
};

export const jtlLink = async (email: string, password: string) => {
  const res = await client.post<{ outcome: string; user_email?: string }>(
    '/auth/jtl/link',
    { email, password }
  );
  return res.data;
};

// The email of the tax session that was open before the JTL callback replaced it, kept across
// the callback's remount in sessionStorage. Only used to tell the person which account was
// signed out when it is not the one the JTL login belongs to.
const PREVIOUS_SESSION_KEY = 'jtl_previous_session_email';

export const rememberPreviousSession = (idToken: string | null) => {
  try {
    if (!idToken) return;
    const payload = JSON.parse(
      atob(idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
    );
    const stray = String(payload['cognito:username'] ?? '')
      .toUpperCase()
      .startsWith('ZITADEL_');
    if (!stray && payload.email)
      sessionStorage.setItem(PREVIOUS_SESSION_KEY, String(payload.email));
  } catch {
    /* storage unavailable or unreadable token: no message, nothing else changes */
  }
};

export const previousSession = (): { email: string } | null => {
  try {
    const email = sessionStorage.getItem(PREVIOUS_SESSION_KEY);
    return email ? { email } : null;
  } catch {
    return null;
  }
};

export const forgetPreviousSession = () => {
  try {
    sessionStorage.removeItem(PREVIOUS_SESSION_KEY);
  } catch {
    /* ignore */
  }
};

export const jtlUnlink = async () => {
  const res = await client.post<{ outcome: string }>('/auth/jtl/unlink');
  return res.data;
};
